import { useEarlyIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import { LaunchDarklyFlag, useLDContext } from 'state/launchdarkly';
import { staticLDFlags } from 'state/launchdarkly/launchdarkly-core';
import { AppTakeover } from 'state/launchdarkly/variations';

import { spongebob } from './spongebob/spongebob';

// TODO define an official real type if we do another takeover
export type AppTakeoverType = typeof spongebob;

// add additional takeovers here
const appTakeoverConfigs: Record<string, AppTakeoverType> = {
  spongebob,
};

/** get a takeover if one is active per LD date ranges */
export const useAppTakeover = (): Partial<
  AppTakeoverType & { isTakeoverActive: boolean; name: string }
> => {
  const ldContext = useLDContext();
  const isDesktop = useEarlyIsDesktopWebBreakpoint();

  if (isDesktop) {
    // disable on desktop, designs assume mobile as is most of our users
    return { isTakeoverActive: false };
  }

  // read from static flags if LD context is not initialized yet since this is used outside of provider in startup animation
  const appTakeover = (ldContext && ldContext.isInitialized
    ? ldContext.flags[LaunchDarklyFlag.APP_TAKEOVER]
    : staticLDFlags.current[LaunchDarklyFlag.APP_TAKEOVER]) as AppTakeover;

  if (appTakeover && appTakeover.length) {
    const now = new Date();
    const activeTakeover = appTakeover.find(takeover => {
      const start = new Date(takeover.startDate);
      const end = new Date(takeover.endDate);
      return now >= start && now <= end;
    });
    if (activeTakeover) {
      return {
        ...appTakeoverConfigs[activeTakeover.name],
        isTakeoverActive: true,
        name: activeTakeover.name,
      };
    }
  }
  return { isTakeoverActive: false };
};

/** return a SHP config if takeover is active for the passed home page */
export const useAppTakeoverSimpleHomePage = (
  homePageId: string
): Partial<AppTakeoverType['homePage'] & { isTakeoverActive: boolean; fontFamily: string }> => {
  const appTakeover = useAppTakeover();

  if (!homePageId) {
    return { isTakeoverActive: false };
  }

  if (appTakeover?.homePage?.targetSimpleHomePageIds?.includes(homePageId)) {
    return {
      ...appTakeover?.homePage,
      isTakeoverActive: true,
      fontFamily: appTakeover?.fontFamily,
    };
  }
  return {};
};

/** returns a menu section config if takeover is active for the passed section */
export const useAppTakeoverMenuSection = (
  sectionId: string
): Partial<AppTakeoverType['menuSection'] & { isTakeoverActive: boolean }> => {
  const appTakeover = useAppTakeover();

  if (!sectionId) {
    return {};
  }

  const isTakeoverSection = appTakeover?.menu?.targetSectionIds?.includes(sectionId);
  if (isTakeoverSection) {
    return { ...appTakeover?.menuSection, isTakeoverActive: true };
  }

  return {};
};

/** returns a PDP config if takeover is active for the menu item */
export const useAppTakeoverPdp = (
  itemId: string
): Partial<AppTakeoverType['pdp'] & { isTakeoverActive: boolean; fontFamily: string }> => {
  const appTakeover = useAppTakeover();

  if (!itemId) {
    return {};
  }

  if (appTakeover?.pdp?.targetMenuItemIds?.includes(itemId)) {
    return { ...appTakeover?.pdp, isTakeoverActive: true, fontFamily: appTakeover?.fontFamily };
  }

  return {};
};
